"use client"

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Paper,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Edit as EditIcon,
  Block as VoidIcon,
  CheckCircle as PaidIcon,
  ArrowBack,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../services/api';
import PageHeader from '../../components/common/PageHeader';
import { useNotification } from '../../contexts/NotificationContext';

const statusColors = {
  draft: 'default',
  open: 'primary',
  paid: 'success',
  void: 'error',
  uncollectible: 'warning',
};

// Helper function to safely format currency
const formatCurrency = (amount, currencyCode = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currencyCode || 'USD').toUpperCase(),
    }).format(amount || 0);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `$${(amount || 0).toFixed(2)}`;
  }
};

const InvoiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/invoices/${id}`);
      if (response.data && response.data.data) {
        setInvoice(response.data.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      showNotification('Error fetching invoice details', 'error');
      navigate('/billing/invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const handleMarkAsPaid = async () => {
    try {
      await api.post(`/invoices/${id}/mark-paid`);
      showNotification('Invoice marked as paid', 'success');
      fetchInvoice();
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      showNotification('Error marking invoice as paid', 'error');
    }
  };

  const handleVoidInvoice = async () => {
    try {
      await api.post(`/invoices/${id}/void`);
      showNotification('Invoice voided', 'success');
      fetchInvoice();
    } catch (error) {
      console.error('Error voiding invoice:', error);
      showNotification('Error voiding invoice', 'error');
    }
  };

  if (loading || !invoice) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        Loading...
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={`Invoice ${invoice.number}`}
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/billing/invoices')}
            >
              Back to Invoices
            </Button>
            {invoice.status === 'draft' && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/billing/invoices/${id}/edit`)}
              >
                Edit
              </Button>
            )}
            {invoice.status === 'open' && (
              <Button
                variant="contained"
                color="success"
                startIcon={<PaidIcon />}
                onClick={handleMarkAsPaid}
              >
                Mark as Paid
              </Button>
            )}
            {invoice.status && ['draft', 'open'].includes(invoice.status) && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<VoidIcon />}
                onClick={handleVoidInvoice}
              >
                Void
              </Button>
            )}
          </Stack>
        }
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Invoice To
                  </Typography>
                  <Typography variant="body1">
                    {invoice.billingDetails?.name || invoice.customerId?.name || 'N/A'}
                  </Typography>
                  {invoice.billingDetails?.address && (
                    <>
                      <Typography variant="body2">
                        {invoice.billingDetails.address.line1}
                      </Typography>
                      {invoice.billingDetails.address.line2 && (
                        <Typography variant="body2">
                          {invoice.billingDetails.address.line2}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        {invoice.billingDetails.address.city},{' '}
                        {invoice.billingDetails.address.state}{' '}
                        {invoice.billingDetails.address.postalCode}
                      </Typography>
                      <Typography variant="body2">
                        {invoice.billingDetails.address.country}
                      </Typography>
                    </>
                  )}
                </Grid>
                <Grid item xs={12} sm={6} sx={{ textAlign: { sm: 'right' } }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Invoice Details
                  </Typography>
                  <Typography variant="body2">
                    Invoice Number: {invoice.number}
                  </Typography>
                  <Typography variant="body2">
                    Date: {invoice.createdAt ? format(new Date(invoice.createdAt), 'MMM dd, yyyy') : 'N/A'}
                  </Typography>
                  {invoice.dueDate && (
                    <Typography variant="body2">
                      Due Date: {format(new Date(invoice.dueDate), 'MMM dd, yyyy')}
                    </Typography>
                  )}
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={invoice.status ? invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1) : 'Unknown'}
                      color={statusColors[invoice.status] || 'default'}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.items && invoice.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.description}</TableCell>
                          <TableCell align="right">{item.quantity || 1}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.unitPrice, invoice.currency)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency((item.quantity || 1) * item.unitPrice, invoice.currency)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box sx={{ mt: 3, textAlign: 'right' }}>
                <Grid container justifyContent="flex-end">
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2">Subtotal:</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            {formatCurrency(invoice.subtotal, invoice.currency)}
                          </Typography>
                        </Grid>
                        {invoice.tax > 0 && (
                          <>
                            <Grid item xs={6}>
                              <Typography variant="subtitle2">Tax:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2">
                                {formatCurrency(invoice.tax, invoice.currency)}
                              </Typography>
                            </Grid>
                          </>
                        )}
                        <Grid item xs={6}>
                          <Typography variant="subtitle2">Total:</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2">
                            {formatCurrency(invoice.total, invoice.currency)}
                          </Typography>
                        </Grid>
                        {invoice.amountPaid > 0 && (
                          <>
                            <Grid item xs={6}>
                              <Typography variant="subtitle2">Amount Paid:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2">
                                {formatCurrency(invoice.amountPaid, invoice.currency)}
                              </Typography>
                            </Grid>
                          </>
                        )}
                        {invoice.amountDue > 0 && (
                          <>
                            <Grid item xs={6}>
                              <Typography variant="subtitle2">Amount Due:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2">
                                {formatCurrency(invoice.amountDue, invoice.currency)}
                              </Typography>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Invoice Timeline
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2">Created</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {invoice.createdAt ? format(new Date(invoice.createdAt), 'MMM dd, yyyy HH:mm') : 'N/A'}
                  </Typography>
                </Box>
                {invoice.status === 'paid' && invoice.paidAt && (
                  <Box>
                    <Typography variant="subtitle2">Paid</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {format(new Date(invoice.paidAt), 'MMM dd, yyyy HH:mm')}
                    </Typography>
                  </Box>
                )}
                {invoice.status === 'void' && invoice.voidedAt && (
                  <Box>
                    <Typography variant="subtitle2">Voided</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {format(new Date(invoice.voidedAt), 'MMM dd, yyyy HH:mm')}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InvoiceDetails;

